/** Serialize saves so edits made during the first INSERT reuse its returned id. */
export function createDraftAutosave<T>(options: {
    draftId?: string;
    save: (value: T, id?: string) => Promise<string>;
    onStatus: (status: string) => void;
}) {
    let id = options.draftId;
    let pending: T | undefined;
    let running: Promise<void> | undefined;
    let stopped = false;
    let latestSnapshot: string | undefined;
    // The first update is the composer's opening state (prefilled reply
    // subject, recipients, restored body). Saving it would create a draft for
    // every reply merely opened, so it only becomes the baseline.
    let baselined = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    async function flush(): Promise<void> {
        clearTimeout(timer);
        if (running) { await running; return flush(); }
        if (stopped || pending === undefined) return;
        const value = pending;
        pending = undefined;
        options.onStatus('Saving draft…');
        running = (async () => {
            try {
                id = await options.save(value, id);
                options.onStatus('Draft saved');
            } catch (error) {
                if (pending === undefined) pending = value;
                options.onStatus('Draft not saved — retry before closing');
                throw error;
            }
        })();
        try { await running; } finally { running = undefined; }
        if (pending !== undefined) await flush();
    }
    return {
        get id() { return id; },
        update(value: T) {
            if (stopped) return;
            const snapshot = JSON.stringify(value);
            if (!baselined) { baselined = true; latestSnapshot = snapshot; return; }
            if (snapshot === latestSnapshot) return;
            latestSnapshot = snapshot;
            pending = value;
            options.onStatus('Unsaved changes');
            clearTimeout(timer);
            timer = setTimeout(() => { void flush().catch(() => {}); }, 700);
        },
        flush,
        stop() { stopped = true; pending = undefined; clearTimeout(timer); },
    };
}
